import { useEffect, useRef, useState } from 'react';
import type { EChartsOption } from 'echarts';
import type { EChartsType } from 'echarts/core';

import type { TimeRange, TimeSeriesChartNavigation } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';

import { animationVisuals } from '../config/chartVisuals';
import { createChartUpdateSnapshot, getChartUpdatePolicy, resetDataZoomRange } from '../update/chartUpdatePolicy';
import {
  type LineAppearances,
  getLineSeriesIds,
  getSettledLineIds,
  updateLineAppearances,
  withoutLineUpdateAnimation,
} from '../update/lineUpdateAnimation';

type UseChartOptionSyncParams = {
  chart: EChartsType | undefined;
  option: EChartsOption;
  timeDomain: TimeRange;
  navigation: TimeSeriesChartNavigation | undefined;
};

/**
 * The wrapper component renders only the very first option. Every later option is applied
 * imperatively with a replaceMerge policy, so unchanged series keep their data alive in ECharts
 * and the user's zoom window survives instead of the chart being torn down on each change.
 *
 * A settled pan or zoom only swaps the data of series that already exist, so it merges with nothing
 * replaced. Lines already on the chart update with animation off (see withoutLineUpdateAnimation),
 * which also spares ECharts the point-by-point diff on every later zoom and pan step.
 *
 * Returns the option to hand to the wrapper; the hook takes care of the rest.
 */
export function useChartOptionSync({ chart, option, timeDomain, navigation }: UseChartOptionSyncParams): EChartsOption {
  const [initialOption] = useState(option);
  const appliedOptionRef = useRef(initialOption);
  const appliedSnapshotRef = useRef(createChartUpdateSnapshot(initialOption, timeDomain, navigation));
  const lineAppearancesRef = useRef<LineAppearances>(new Map());

  // Declared before the update below, so the lines of the first render count from when they appeared.
  useEffect(() => {
    if (chart) {
      lineAppearancesRef.current = updateLineAppearances(
        new Map(),
        getLineSeriesIds(appliedOptionRef.current),
        performance.now()
      );
    }
  }, [chart]);

  useEffect(() => {
    if (!chart || chart.isDisposed() || option === appliedOptionRef.current) {
      return;
    }

    const now = performance.now();
    const snapshot = createChartUpdateSnapshot(option, timeDomain, navigation);
    const policy = getChartUpdatePolicy(appliedSnapshotRef.current, snapshot);
    const isDataOnlyUpdate = policy.replaceMerge.length === 0;
    const settledLineIds = getSettledLineIds(lineAppearancesRef.current, now, animationVisuals.initialDurationMs);
    const update = policy.shouldResetDataZoomRange ? resetDataZoomRange(option) : option;

    chart.setOption(withoutLineUpdateAnimation(update, settledLineIds), {
      replaceMerge: policy.replaceMerge,
      ...(isDataOnlyUpdate && { silent: true }),
    });

    appliedOptionRef.current = option;
    appliedSnapshotRef.current = snapshot;
    lineAppearancesRef.current = updateLineAppearances(lineAppearancesRef.current, getLineSeriesIds(option), now);
  }, [chart, navigation, option, timeDomain]);

  return initialOption;
}
